import { Helmet } from 'react-helmet-async';
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  useTheme,
  CircularProgress
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';
import { LandingLayout } from '../components/landing/LandingLayout';
import { Footer } from '../components/landing/Footer';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useStripeCheckout } from '../hooks/useStripeCheckout';

const tiers = [
  {
    title: 'Free',
    price: '$0',
    description: 'For learners who want to organize their material manually.',
    features: [
      '5 Brain loops',
      '10 cards per loop',
      'Standard progress insights',
      'Study history'
    ],
    buttonText: 'Start for Free',
    buttonVariant: 'outlined',
    popular: false
  },
  {
    title: 'Pro',
    price: '$9.99',
    period: '/month',
    description: 'Unlock AI that builds, improves, and personalizes every study session.',
    features: [
      'Unlimited Brain loops',
      'Unlimited cards per loop',
      'AI Study Loop Generator',
      'Note taking with AI Brain Loop creation',
      'Advanced analytics & mastery forecasts',
      'Priority support',

    ],
    buttonText: 'Unlock AI Learning',
    buttonVariant: 'contained',
    popular: true
  }
];

const PricingContent = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { startCheckout, isCheckoutLoading } = useStripeCheckout();

  const handleTierSelect = async (tierTitle: string) => {
    if (tierTitle === 'Pro') {
      if (!user) {
        navigate('/signup');
        return;
      }
      await startCheckout();
      return;
    }

    return;
  };

  return (
    <Box sx={{ py: 8, bgcolor: 'background.default' }}>
      <Helmet>
        <title>BrainLoop Pricing - Free & Pro Plans for AI-Powered Study</title>
        <meta name="description" content="Start free with BrainLoop or upgrade to Pro for $9.99/month. Unlock unlimited AI-generated flashcard loops, advanced analytics, and priority support." />
        <link rel="canonical" href="https://brainloop.app/pricing" />
        <meta property="og:title" content="BrainLoop Pricing - Free & Pro Plans for AI-Powered Study" />
        <meta property="og:description" content="Start free or upgrade to Pro for $9.99/month. Unlock unlimited AI-generated flashcard loops, advanced analytics, and priority support." />
        <meta property="og:url" content="https://brainloop.app/pricing" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          "name": "BrainLoop Pro",
          "description": "AI-powered study loop app with unlimited flashcard generation, advanced analytics, and priority support.",
          "offers": [
            {
              "@type": "Offer",
              "name": "Free",
              "price": "0",
              "priceCurrency": "USD",
              "description": "5 Brain loops, 10 cards per loop, standard progress insights."
            },
            {
              "@type": "Offer",
              "name": "Pro",
              "price": "9.99",
              "priceCurrency": "USD",
              "priceSpecification": {
                "@type": "UnitPriceSpecification",
                "billingDuration": "P1M"
              },
              "description": "Unlimited Brain loops, AI Study Loop Generator, advanced analytics, and priority support."
            }
          ]
        })}</script>
      </Helmet>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            variant="h2"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 700,
              background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.dark} 90%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Simple, Transparent Pricing
          </Typography>
          <Typography variant="h5" color="text.secondary" sx={{ maxWidth: '800px', mx: 'auto' }}>
            Choose the plan that best fits your learning needs
          </Typography>
        </Box>

        <Box sx={{ mt: 4 }}>
          <Grid container spacing={4}>
            {tiers.map((tier) => (
              <Grid 
                item 
                xs={12} 
                md={6} 
                key={tier.title}
                sx={{
                  position: 'relative',
                  pt: tier.popular ? '24px !important' : undefined
                }}
              >
                {tier.popular && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      background: `linear-gradient(145deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
                      color: 'primary.contrastText',
                      px: 4,
                      py: 1,
                      borderRadius: 1.667,
                      fontSize: 'body2.fontSize',
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                      zIndex: 2,
                      boxShadow: [
                        '0 6px 16px rgba(99,102,241,0.5)',
                        '0 2px 6px rgba(0,0,0,0.15)',
                        'inset 0 1.5px 0 rgba(255,255,255,0.3)',
                        'inset 0 -1.5px 0 rgba(0,0,0,0.15)',
                      ].join(', '),
                    }}
                  >
                    Most Popular
                  </Box>
                )}
                {(() => {
                  const isProTier = tier.title === 'Pro';
                  const proGradient = theme.palette.mode === 'dark'
                    ? `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.95)} 0%, rgba(236,72,153,0.95) 100%)`
                    : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 1)} 0%, rgba(236,72,153,0.95) 100%)`;
                  const secondaryTextColor = isProTier ? alpha(theme.palette.primary.contrastText, 0.85) : 'text.secondary';
                  const listTextColor = isProTier ? alpha(theme.palette.primary.contrastText, 0.95) : undefined;
                  const checkIconColor = isProTier ? theme.palette.primary.light : theme.palette.primary.main;

                  return (
                    <Card
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'relative',
                        transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                        overflow: 'hidden',
                        '&:hover': {
                          transform: 'translateY(-12px) scale(1.01)',
                          boxShadow: isProTier
                            ? [
                                '0 36px 70px rgba(99,102,241,0.55)',
                                '0 12px 24px rgba(0,0,0,0.2)',
                                'inset 0 3px 0 rgba(255,255,255,0.28)',
                                'inset 0 -3px 0 rgba(0,0,0,0.22)',
                              ].join(', ')
                            : [
                                '0 24px 48px rgba(99,102,241,0.14)',
                                '0 8px 16px rgba(0,0,0,0.08)',
                                'inset 0 2px 0 rgba(255,255,255,0.9)',
                                'inset 0 -2px 0 rgba(0,0,0,0.06)',
                              ].join(', '),
                        },
                        bgcolor: isProTier ? 'transparent' : 'background.paper',
                        backgroundImage: isProTier ? proGradient : 'none',
                        color: isProTier ? 'primary.contrastText' : 'inherit',
                        border: isProTier
                          ? `1px solid ${alpha(theme.palette.common.white, 0.18)}`
                          : `1px solid ${theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.1) : alpha(theme.palette.common.black, 0.07)}`,
                        boxShadow: isProTier
                          ? [
                              '0 28px 60px rgba(99,102,241,0.45)',
                              '0 10px 20px rgba(0,0,0,0.18)',
                              'inset 0 3px 0 rgba(255,255,255,0.25)',
                              'inset 0 -3px 0 rgba(0,0,0,0.2)',
                            ].join(', ')
                          : [
                              '0 4px 16px rgba(0,0,0,0.06)',
                              'inset 0 1.5px 0 rgba(255,255,255,0.9)',
                              'inset 0 -1.5px 0 rgba(0,0,0,0.05)',
                            ].join(', '),
                        ...(tier.popular ? {
                          minHeight: 75,
                          zIndex: 1,
                        } : {
                          minHeight: 68.75
                        })
                      }}
                    >
                  <CardContent sx={{ flexGrow: 1, p: 4 }}>
                    <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 700 }}>
                      {tier.title}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 2 }}>
                      <Typography variant="h3" component="span" sx={{ fontWeight: 700 }}>
                        {tier.price}
                      </Typography>
                      {tier.period && (
                        <Typography variant="subtitle1" sx={{ ml: 1, color: secondaryTextColor }}>
                          {tier.period}
                        </Typography>
                      )}
                    </Box>
                    <Typography variant="subtitle1" color={secondaryTextColor} sx={{ mb: 4 }}>
                      {tier.description}
                    </Typography>
                    {tier.title === 'Pro' && (
                      <Typography
                        variant="subtitle2"
                        color={isProTier ? alpha(theme.palette.primary.contrastText, 0.9) : 'primary'}
                        sx={{
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                          mb: 2
                        }}
                      >
                        Everything in Free, plus all of these upgrades:
                      </Typography>
                    )}
                    <List sx={{ mb: 4 }}>
                      {tier.features.map((feature) => (
                        <ListItem key={feature} sx={{ py: 1 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                              <CheckIcon sx={{ color: checkIconColor }} />
                          </ListItemIcon>
                          <ListItemText 
                            primary={feature} 
                            sx={{
                              '& .MuiTypography-root': {
                                  fontSize: 'body1.fontSize',
                                  fontWeight: 500,
                                  color: listTextColor,
                              }
                            }} 
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                      <Box sx={{ p: 4, pt: 0 }}>
                    {tier.title === 'Free' ? (
                      <Box
                        sx={{
                          py: 1.5,
                          fontSize: 'body1.fontSize',
                          fontWeight: 600,
                          textAlign: 'center',
                          borderRadius: 2,
                          border: `1px solid ${theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.2) : alpha(theme.palette.common.black, 0.1)}`,
                          color: secondaryTextColor,
                        }}
                      >
                        Current Tier
                      </Box>
                    ) : (
                      <Button
                        fullWidth
                        variant={tier.buttonVariant as 'contained' | 'outlined'}
                        size="large"
                        sx={{
                          py: 1.5,
                          fontSize: 'body1.fontSize',
                          fontWeight: 700,
                          borderRadius: 1.667,
                          background: `linear-gradient(145deg, ${alpha(theme.palette.common.white, 0.95)}, ${alpha(theme.palette.common.white, 0.85)})`,
                          color: theme.palette.primary.main,
                          boxShadow: [
                            '0 8px 20px rgba(0,0,0,0.2)',
                            '0 3px 8px rgba(0,0,0,0.12)',
                            'inset 0 1.5px 0 rgba(255,255,255,1)',
                            'inset 0 -1.5px 0 rgba(0,0,0,0.08)',
                          ].join(', '),
                          transition: 'transform 0.18s ease, box-shadow 0.18s ease',
                          '&:hover': {
                            background: `linear-gradient(145deg, ${alpha(theme.palette.common.white, 1)}, ${alpha(theme.palette.common.white, 0.92)})`,
                            boxShadow: [
                              '0 10px 26px rgba(0,0,0,0.25)',
                              '0 4px 10px rgba(0,0,0,0.14)',
                              'inset 0 1.5px 0 rgba(255,255,255,1)',
                              'inset 0 -1.5px 0 rgba(0,0,0,0.1)',
                            ].join(', '),
                            transform: 'translateY(-1px)',
                          },
                          '&:active': {
                            transform: 'translateY(1px)',
                            boxShadow: '0 4px 10px rgba(0,0,0,0.18)',
                          },
                        }}
                        onClick={() => handleTierSelect(tier.title)}
                        disabled={tier.title === 'Pro' && isCheckoutLoading}
                      >
                        {isCheckoutLoading ? (
                          <>
                            <CircularProgress size={20} sx={{ mr: 1 }} />
                            Redirecting…
                          </>
                        ) : (
                          tier.buttonText
                        )}
                      </Button>
                    )}
                      </Box>
                    </Card>
                  );
                })()}
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>
      <Container maxWidth="lg">
      <Card
        sx={{
          mt: 8,
          p: { xs: 3, md: 5 },
          borderRadius: 1.667,
        background: theme.palette.mode === 'dark'
            ? `linear-gradient(145deg, ${theme.palette.secondary.dark}, ${theme.palette.secondary.main})`
            : `linear-gradient(145deg, ${alpha(theme.palette.primary.light, 0.3)}, ${theme.palette.primary.light})`,
          boxShadow: theme.palette.mode === 'dark'
            ? [
                '0 16px 40px rgba(0,0,0,0.4)',
                'inset 0 2px 0 rgba(255,255,255,0.07)',
                'inset 0 -2px 0 rgba(0,0,0,0.2)',
              ].join(', ')
            : [
                '0 16px 40px rgba(99,102,241,0.14)',
                '0 4px 12px rgba(0,0,0,0.05)',
                'inset 0 2px 0 rgba(255,255,255,0.8)',
                'inset 0 -2px 0 rgba(99,102,241,0.08)',
              ].join(', '),
        }}
      >
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
              Why go Pro now?
            </Typography>
            <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3 }}>
              Pro members finish decks 4× faster thanks to automated creation, AI coaching,
              and actionable insights that keep you consistent.
            </Typography>
            <List>
              {[
                'Generate polished decks from docs, lectures, or notes instantly.',
                'Let AI surface weak spots and schedule hyper-focused reviews.',
                'Get persuasive study nudges, summaries, and explanations on demand.'
              ].map((benefit) => (
                <ListItem key={benefit} sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <CheckIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary={benefit} />
                </ListItem>
              ))}
            </List>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card
              elevation={0}
              sx={{
                bgcolor: 'background.paper',
                borderRadius: 1.667,
                p: 4,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                boxShadow: theme.palette.mode === 'dark'
                  ? [
                      '0 20px 40px rgba(0,0,0,0.6)',
                      'inset 0 2px 0 rgba(255,255,255,0.06)',
                      'inset 0 -2px 0 rgba(0,0,0,0.2)',
                    ].join(', ')
                  : [
                      '0 20px 40px rgba(99,102,241,0.14)',
                      '0 6px 12px rgba(0,0,0,0.06)',
                      'inset 0 2px 0 rgba(255,255,255,0.95)',
                      'inset 0 -2px 0 rgba(0,0,0,0.06)',
                    ].join(', '),
              }}
            >
              <Stack spacing={2}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  Ready for AI that studies with you?
                </Typography>
                <Typography color="text.secondary">
                  Upgrade once, cancel anytime. Your progress, decks, and preferences carry over instantly.
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  sx={{
                    py: 1.5,
                    fontWeight: 700,
                    borderRadius: 1.667,
                    background: `linear-gradient(145deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
                    boxShadow: [
                      '0 8px 20px rgba(99,102,241,0.45)',
                      '0 3px 8px rgba(0,0,0,0.12)',
                      'inset 0 1.5px 0 rgba(255,255,255,0.28)',
                      'inset 0 -1.5px 0 rgba(0,0,0,0.14)',
                    ].join(', '),
                    transition: 'transform 0.18s ease, box-shadow 0.18s ease',
                    '&:hover': {
                      background: `linear-gradient(145deg, ${theme.palette.primary.main}, ${theme.palette.secondary.dark})`,
                      boxShadow: [
                        '0 10px 26px rgba(99,102,241,0.55)',
                        '0 4px 10px rgba(0,0,0,0.15)',
                        'inset 0 1.5px 0 rgba(255,255,255,0.25)',
                        'inset 0 -1.5px 0 rgba(0,0,0,0.16)',
                      ].join(', '),
                      transform: 'translateY(-1px)',
                    },
                    '&:active': {
                      transform: 'translateY(1px)',
                      boxShadow: '0 4px 10px rgba(99,102,241,0.35)',
                    },
                  }}
                  onClick={() => handleTierSelect('Pro')}
                  disabled={isCheckoutLoading}
                >
                  {isCheckoutLoading ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Redirecting…
                    </>
                  ) : (
                    'Start my AI trial'
                  )}
                </Button>
                <Typography variant="caption" color="text.secondary">
                  Includes a 7‑day refund window if it’s not the right fit.
                </Typography>
              </Stack>
            </Card>
          </Grid>
        </Grid>
      </Card>
      </Container>
    </Box>
  );
};

export const PricingPage = () => (
  <LandingLayout>
    <PricingContent />
    <Footer />
  </LandingLayout>
);

export const PricingPageApp = () => (
  <Box sx={{ width: '100%' }}>
    <PricingContent />
  </Box>
);